import Konva from "konva";
import { Box } from "konva/types/shapes/Transformer";
import { rotate } from "./snap-utils";

type Point = { x: number; y: number };

export type Degrees = number & { __unit: "degrees" };
export type Radians = number & { __unit: "radians" };

const Deg2Rad = Math.PI / 180;

export function toRadians(degrees: Degrees) {
  return (degrees * Deg2Rad) as Radians;
}
export function toDegrees(radians: Radians) {
  return (radians / Deg2Rad) as Degrees;
}

export type PointAndDirection = Point & { rotation: Degrees };

export type CanvasElementBox = {
  x: number;
  y: number;
  width: number;
  height: number;
  rotate?: Degrees;
};

// function angle(a: number, unit: "degrees" | "radians") {
//   return (unit == "degrees" ? a * Deg2Rad : a) as Radians;
// }

export function konvaTransformForElement(element: any) {
  const { x, y, scaleX = 1, scaleY = 1, rotate = 0 } = element;
  const transform = new Konva.Transform();
  transform.translate(x, y);
  transform.rotate(toRadians(rotate as Degrees));
  transform.scale(scaleX, scaleY);
  return transform;
}

export function roundToPrecision(value: number, precision: number = 6): number {
  const factor = Math.pow(10, precision);
  return Math.round(value * factor) / factor;
}

export function roundBox(box: Box): Box {
  return {
    ...box,
    x: roundToPrecision(box.x),
    y: roundToPrecision(box.y),
    width: roundToPrecision(box.width),
    height: roundToPrecision(box.height),
    rotation: roundToPrecision(box.rotation, 8), // Higher precision for rotation
  };
}

export function safeRotate(x: number, y: number, angle: number, centerX: number, centerY: number): [number, number] {
  const result = rotate(x, y, angle, centerX, centerY);
  return [roundToPrecision(result[0]), roundToPrecision(result[1])];
}

export function safeDivide(numerator: number, denominator: number, fallback: number = 1): number {
  if (Math.abs(denominator) < 1e-10) {
    return fallback;
  }
  return roundToPrecision(numerator / denominator);
}

export default class Transform {
  private x: number;
  private y: number;
  private scaleX: number;
  private scaleY: number;
  private rotate: Radians;
  private _matrix?: [number, number, number, number, number, number];
  private invSx: number;
  private invSy: number;

  constructor(x: number, y: number, scaleX = 1, scaleY = 1, rotate = 0 as Degrees) {
    this.x = x;
    this.y = y;
    this.scaleX = scaleX;
    this.scaleY = scaleY;
    this.rotate = toRadians(rotate);
    this.invSx = 1 / scaleX;
    this.invSy = 1 / scaleY;
  }

  /**
   * Apply transformation to a point based on the properties of tr
   * @param tr CanvasElementBox, rotation, scale, taken from here
   * @param p Point to apply transformation to
   * @param out Optional object that will be modified in-place
   * @returns Point after transformation
   */
  static Point(tr: CanvasElementBox, p: Point, out?: Point) {
    let { x, y } = p;
    x *= tr.width;
    y *= tr.height;
    if (tr.rotate) {
      const rad = toRadians(tr.rotate as Degrees);
      const cos = Math.cos(rad),
        sin = Math.sin(rad);
      const x1 = x * cos - y * sin;
      const y1 = x * sin + y * cos;
      x = x1;
      y = y1;
    }
    x += tr.x;
    y += tr.y;
    if (out) {
      out.x = x;
      out.y = y;
      return out;
    }
    return { x, y };
  }

  /**
   * Apply inverse transformation to a point based on the properties of tr
   * @param tr CanvasElementBox, rotation, scale, taken from here
   * @param p Point to apply inverse transformation to
   * @param out Optional object that will be modified in-place
   * @returns Point after transformation
   */
  static InvPoint(tr: CanvasElementBox, p: Point, out?: Point) {
    let { x, y } = p;
    x -= tr.x;
    y -= tr.y;
    if (tr.rotate) {
      const rad = toRadians(-tr.rotate as Degrees);
      const cos = Math.cos(rad),
        sin = Math.sin(rad);
      const x1 = x * cos - y * sin;
      const y1 = x * sin + y * cos;
      x = x1;
      y = y1;
    }
    x /= tr.width;
    y /= tr.height;
    if (out) {
      out.x = x;
      out.y = y;
      return out;
    }
    return { x, y };
  }

  get matrix() {
    return (this._matrix ??= this._calculateMatrix());
  }

  private _calculateMatrix(): [number, number, number, number, number, number] {
    const result = new Array<number>(6);
    const cos = this.rotate ? Math.cos(this.rotate) : 1;
    const sin = this.rotate ? Math.sin(this.rotate) : 0;
    result[0] = this.scaleX * cos;
    result[1] = this.scaleY * -sin;
    result[2] = this.x;
    result[3] = this.scaleX * sin;
    result[4] = this.scaleY * cos;
    result[5] = this.y;
    return result as [number, number, number, number, number, number];
  }

  public transformPoint<T extends Point>(p: T, out?: T) {
    const { x, y } = p;
    const m = this.matrix;
    const x1 = x * m[0] + y * m[1] + m[2];
    const y1 = x * m[3] + y * m[4] + m[5];
    if (out != undefined) {
      out.x = x1;
      out.y = y1;
      return out;
    }
    return { x: x1, y: y1 } as T;
  }

  public transformAngle(angle: Degrees) {
    // when angle is multiple of 90 deg, the normal is aligned to the axes,
    // so non-uniform scaling doesn't affect it. In this case just add the rotation
    const deg = toDegrees(this.rotate);
    if (angle % 90 == 0) {
      return (angle + deg) as Degrees;
    }
    // normals under non-uniform scaling need to be transformed with the inverse of the scale
    const radians = toRadians(angle);
    let x = Math.cos(radians),
      y = Math.sin(radians);
    x *= this.invSx;
    y *= this.invSy;
    const newRotationDegrees = toDegrees(Math.atan2(y, x) as Radians);
    return (newRotationDegrees + deg) as Degrees;
  }

  public transformPointAndDirection(p: PointAndDirection, out?: PointAndDirection) {
    const result = this.transformPoint(p, out);
    const newAngle = this.transformAngle(p.rotation);
    result.rotation = newAngle;
    return result;
  }
}
